import { useCallback } from 'react';
import { getHoaDonBanHangErrorMessage } from '../utils';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import { toast } from '@/hooks';
import api from '@/lib/api';

interface UseHoaDonBanHangMutationProps {
  detail: BanHangRecord | null;
  formMode: 'add' | 'edit' | 'view';
  addItem: (data: BanHangPayload) => Promise<BanHangRecord>;
  updateItem: (uuid: string, data: BanHangPayload) => Promise<BanHangRecord>;
  deleteItem: (uuid: string) => Promise<BanHangRecord>;
  deleteGiayBaoCo: (uuid: string) => Promise<BanHangRecord>;
  deletePhieuThu: (uuid: string) => Promise<BanHangRecord>;
  handleCloseForm: () => void;
  handleCloseDelete: () => void;
  clearSelection: () => void;
  refreshData: () => Promise<void>;
}

export const useHoaDonBanHangMutation = ({
  detail,
  formMode,
  addItem,
  updateItem,
  deleteItem,
  deleteGiayBaoCo,
  deletePhieuThu,
  handleCloseForm,
  handleCloseDelete,
  clearSelection,
  refreshData
}: UseHoaDonBanHangMutationProps) => {
  const { entity } = useAuth();

  const handleDelete = useCallback(async () => {
    if (!detail) return;

    await deleteItem(detail.uuid);
    handleCloseDelete();
    clearSelection();
    handleCloseForm();
    await refreshData();
  }, [clearSelection, deleteItem, detail, handleCloseDelete, handleCloseForm, refreshData]);

  const handleBulkDelete = useCallback(
    async (uuids: string[]) => {
      try {
        await api.post(`/entities/${entity?.slug}/erp/${QUERY_KEYS.HOA_DON_BAN_HANG}/bulk-delete/`, {
          list: uuids
        });
        clearSelection();
        await refreshData();
      } catch (error) {
        console.error('Bulk delete error:', error);
        throw error;
      }
    },
    [clearSelection, entity?.slug, refreshData]
  );

  const handleSubmit = useCallback(
    async (values: BanHangPayload) => {
      const { data } = values;

      try {
        if (formMode === 'add') {
          await addItem(data);
        } else if (formMode === 'edit' && detail) {
          await updateItem(detail.uuid, data);

          if (!data.pt_tao_yn && detail.ma_httt === 'CKB' && detail.pt_tao_yn && detail.giay_bao_co_uuids[0]) {
            await deleteGiayBaoCo(detail.giay_bao_co_uuids[0]);
          } else if (!data.pt_tao_yn && detail.ma_httt === 'TMB' && detail.pt_tao_yn && detail.phieu_thu_uuids[0]) {
            await deletePhieuThu(detail.phieu_thu_uuids[0]);
          }
        }

        handleCloseForm();
        clearSelection();
        await refreshData();
      } catch (error: unknown) {
        toast({
          description: getHoaDonBanHangErrorMessage(error),
          variant: 'destructive'
        });
      }
    },
    [
      addItem,
      clearSelection,
      deleteGiayBaoCo,
      deletePhieuThu,
      detail,
      formMode,
      handleCloseForm,
      refreshData,
      updateItem
    ]
  );

  return {
    handleDelete,
    handleBulkDelete,
    handleSubmit
  };
};
